import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Button, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap";
import { Colxx } from "../../../components/common/CustomBootstrap";
import ActiveHelper from "../../../components/common/activeHelper";
import { firestore } from '../../../helpers/Firebase';
import { createNotification } from "../../../helpers/Utils";
import Voucher from "./components/marketingVoucher";

const Vouchers = () => {
  const { user } = useSelector(state => state.authUser);
  const { vouchers } = useSelector(state => state.providers);
  const search = useSelector(state => state.settings.search) || '';
  const [isLoading, setIsLoading] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState(false);
  const [filteredVoucherList, setFilteredVoucherList] = useState([]);
  const [deleteVoucher, setDeleteVoucher] = useState(false);

  // Convert to MainPage tableColumns format
  const tableColumns = useMemo(() => [
    {
      Header: "Title",
      accessor: "title",
      type: 'label'
    },
    {
      Header: "Amount",
      accessor: "amount",
      type: 'amount'
    },
    {
      Header: "Created",
      accessor: "created",
      type: 'label',
      formatValue: (value) => value ? moment(value).format('DD/MM/YYYY') : '--'
    },
    {
      Header: "Expiration Date",
      accessor: "exp",
      type: 'label',
      formatValue: (value) => value ? moment(value).format('DD/MM/YYYY') : '--'
    },
    {
      Header: 'Splits',
      accessor: 'numberOfSplits',
      type: 'label'
    },
    {
      Header: "Claimed",
      accessor: "claimed",
      type: 'claimed'
    }
  ], []);

  // Filter vouchers based on search
  useEffect(() => {
    let voucherList = vouchers ? Object.values(vouchers) : [];

    // Apply search filter
    if (search && search.trim()) {
      const searchLower = search.toLowerCase().trim();
      voucherList = voucherList.filter(voucher => {
        return (
          (voucher?.title?.toLowerCase().includes(searchLower)) ||
          (voucher?.amount?.toString().includes(searchLower))
        );
      });
    }

    setFilteredVoucherList(voucherList);
  }, [vouchers, search]);

  const deleteVoucherHandler = useCallback((voucher) => {
    let ref = firestore().collection('vouchers').doc(voucher?.key)
    ref.delete().then(() => {
      setDeleteVoucher(false);
      setIsLoading(false);
    });
  }, []);

  // Custom delete function to handle delete modal
  const handleDelete = useCallback((voucher) => {
    setDeleteVoucher(voucher);
  }, []);

  // Custom edit function to handle copy link (no edit needed for marketing vouchers)
  const handleEdit = useCallback((voucher) => {
    // Handle copy link action
    if (voucher?.link) {
      navigator.clipboard.writeText(voucher.link);
      createNotification('success', 'Link copied to clipboard');
    }
  }, []);

  // Custom create function to handle create modal
  const handleCreate = useCallback(() => {
    setSelectedVoucher({ addNew: true });
  }, []);

  return (
    <>
      {isLoading && (
        <div className="loading" />
      )}

      {selectedVoucher && (
        <Voucher
          selectedVoucher={selectedVoucher}
          dismiss={() => {
            setSelectedVoucher(false);
            setIsLoading(false);
          }}
        />
      )}

      <Modal backdrop="static" isOpen={!!deleteVoucher}>
        <ModalHeader>
          Delete Voucher
        </ModalHeader>
        <ModalBody>
          <Row>
            <Colxx xxs="12">
              <Label>Are you sure you want to delete this Voucher?</Label>
            </Colxx>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button onClick={() => {
            setDeleteVoucher(false);
          }}>Dismiss</Button>
          <Button color='danger' onClick={() => {
            setIsLoading(true);
            deleteVoucherHandler(deleteVoucher);
          }}>Delete Voucher</Button>
        </ModalFooter>
      </Modal>

      <ActiveHelper
        title="Marketing Vouchers"
        selected="vouchers"
        data={filteredVoucherList}
        tableColumns={tableColumns}
        permenatDelete={true}
        defaultSorting="created"
        sortDirection="desc"
        renderAsModal={true}
        modalSize="lg"
        modalBackdrop="static"
        hideDefaultDeleteButton={false}
        customDeleteFunction={handleDelete}
        customDeleteTitle="Delete"
        customEditFunction={handleEdit}
        customEditTitle="Copy Link"
        customCreateFunction={handleCreate}
        showCreateButton={true}
        user={user}
      />
    </>
  );
};



export default Vouchers;
